import styled, { css } from 'styled-components';
import { People } from 'src/graphql/schema.graphql';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { media } from 'src/styles/designs';
import { Avatar } from 'src/modules/user/components/Avatar';
import { StyleProps } from '@summer/jst-react';
import { lineClamp } from 'src/styles/common';
import { TrackedLink } from 'src/components/TrackedLink';
import { route } from 'src/services/route';
import { Skeleton } from 'src/components/common/Skeleton';

export interface SpeakerTileProps {
  speaker: People;
  variant?: 'primary' | 'secondary';
}

const Container = styled.a<{ $variant?: SpeakerTileProps['variant'] }>`
  display: grid;
  grid-template: 'avatar text';
  grid-template-columns: 35px 1fr;
  grid-gap: 16px;

  padding: 16px;
  transition: 0.25s;

  &:hover {
    background: ${({ theme }) => theme.palette.background};
  }

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 32px 20px 50px;
    min-width: 210px;
    max-width: 270px;

    grid-template: 'avatar' 'text';
    grid-template-rows: 85px 1fr;
    justify-items: center;
    height: 100%;
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'secondary':
        return css`
          ${media.w.lessThan.desktop} {
            padding: 32px 20px 50px;

            grid-template: 'avatar' 'text';
            grid-template-rows: 85px 1fr;
            justify-items: center;
            width: 100%;
          }
        `;
    }
  }}
`;

const Figcaption = styled.figcaption<{
  $variant?: SpeakerTileProps['variant'];
}>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  font-size: 14px;
  line-height: 18px;

  color: ${({ theme }) => theme.text.palette.primary};

  ${({ $variant }) => {
    switch ($variant) {
      case 'primary':
        return css`
          ${media.w.greaterThanOrEqual.desktop} {
            max-width: 170px;
            text-align: center;
          }
        `;
      case 'secondary':
        return css`
          max-width: 170px;
          text-align: center;
        `;
    }
  }}
`;

const Text = styled.p`
  font-weight: 300;
  word-wrap: break-word;
  ${lineClamp(6)};
`;

const Name = styled(Text)`
  font-weight: 500;
`;

const Country = styled(Text)`
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.palette.accent};
`;

const StyledAvatar = styled(Avatar)<{ $variant: SpeakerTileProps['variant'] }>`
  ${({ $variant }) => {
    switch ($variant) {
      case 'primary':
        return css`
          width: 35px;
          height: 35px;

          ${media.w.greaterThanOrEqual.desktop} {
            width: 78px;
            height: 78px;
          }
        `;
      case 'secondary':
        return css`
          width: 78px;
          height: 78px;
        `;
    }
  }}
`;

export const SpeakerTile = ({
  speaker: _speaker,
  variant = 'primary',
  ...props
}: SpeakerTileProps & StyleProps) => {
  const translateItem = useTranslateItem();

  const speaker = translateItem(_speaker);

  return (
    <TrackedLink
      href={route.search({ speaker: speaker.name }).videos()}
      passHref
    >
      <Container $variant={variant} {...props}>
        <StyledAvatar
          $variant={variant}
          src={speaker.avatar?.id}
          sizes="100px"
        />
        <Figcaption $variant={variant}>
          <Name>
            {speaker.degree ? `${speaker.degree}\u00a0` : ''}
            {speaker.name}
          </Name>
          <Text>{speaker.title}</Text>
          {speaker.country && (
            <Country>{translateItem(speaker.country).name}</Country>
          )}
        </Figcaption>
      </Container>
    </TrackedLink>
  );
};

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 16px;
`;

export const SpeakerTileSkeleton = (props: StyleProps) => {
  return (
    <SkeletonContainer as="div" {...props}>
      <Skeleton
        width="78px"
        height="78px"
        style={{ borderRadius: '50%', marginBottom: '20px' }}
      />
      <Skeleton width="120px" height="20px" />
      <Skeleton width="160px" height="20px" />
    </SkeletonContainer>
  );
};
